import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function MeetPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingCode, setMeetingCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleCreateMeeting = async () => {
    setError('');
    setCreating(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'create-public-meeting', title: 'Réunion rapide' },
      });

      if (fnError || data?.error) {
        setError(data?.error || 'Erreur lors de la création de la réunion.');
        return;
      }

      const code = data?.meeting?.code;
      if (code) {
        setMeetingCode(code);
        const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
        const pathPrefix = basePath ? `/${basePath}` : '';
        const fullLink = `${window.location.origin}${pathPrefix}/meet/${code}`;
        setMeetingLink(fullLink);
      }
    } catch {
      setError('Erreur serveur. Réessayez.');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(meetingLink).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        const input = document.createElement('input');
        input.value = meetingLink;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
    );
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Rejoins ma visioconférence : ${meetingLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const joinMeeting = () => {
    navigate(`/meet/${meetingCode}`);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-12 h-12 rounded-2xl bg-clinic-blue/10 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-clinic-blue"></i>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center py-10 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-lg mx-auto">
          {!meetingLink ? (
            /* État initial : bouton créer */
            <div className="text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-clinic-green/10 to-clinic-green/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-vidicon-line text-5xl text-clinic-green"></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-clinic-blue-dark mb-3">
                Lien de visioconférence
              </h1>
              <p className="text-gray-600 mb-8 max-w-sm mx-auto">
                Créez un lien en un clic, partagez-le sur WhatsApp. Toute personne qui clique rejoint directement la réunion, sans compte.
              </p>

              <button
                onClick={handleCreateMeeting}
                disabled={creating}
                className="w-full sm:w-auto px-8 py-4 bg-clinic-green hover:bg-clinic-green-dark text-white rounded-2xl transition-all font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 mx-auto shadow-sm hover:shadow-md whitespace-nowrap"
              >
                {creating ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    Création...
                  </>
                ) : (
                  <>
                    <i className="ri-add-circle-line text-xl"></i>
                    Créer un lien de réunion
                  </>
                )}
              </button>

              {error && (
                <p className="mt-4 text-sm text-red-500 flex items-center justify-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </p>
              )}

              <div className="mt-10 pt-8 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Comment ça marche ?</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-clinic-green/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-clinic-green">1</span>
                    </div>
                    <p className="text-xs text-gray-600">Créez le lien</p>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-clinic-green/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-clinic-green">2</span>
                    </div>
                    <p className="text-xs text-gray-600">Partagez sur WhatsApp</p>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-clinic-green/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-clinic-green">3</span>
                    </div>
                    <p className="text-xs text-gray-600">Ils cliquent et rejoignent</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* État succès : lien créé */
            <div className="text-center">
              <div className="w-20 h-20 bg-clinic-green/10 rounded-3xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-check-line text-4xl text-clinic-green"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-clinic-blue-dark mb-2">
                Votre lien est prêt !
              </h2>
              <p className="text-gray-600 mb-6">
                Partagez ce lien, toute personne qui clique dessus pourra rejoindre la visioconférence.
              </p>

              {/* Lien affiché */}
              <div className="bg-gray-50 border-2 border-clinic-green/30 rounded-2xl p-4 mb-5">
                <p className="text-xs text-gray-500 mb-2 font-medium">LIEN DE LA RÉUNION</p>
                <p className="text-sm font-mono text-clinic-blue-dark break-all select-all mb-3">
                  {meetingLink}
                </p>
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <button
                    onClick={copyToClipboard}
                    className="flex-1 px-4 py-2.5 bg-clinic-blue hover:bg-clinic-blue/90 text-white rounded-xl transition-colors font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                    {copied ? 'Copié !' : 'Copier le lien'}
                  </button>
                  <button
                    onClick={shareWhatsApp}
                    className="flex-1 px-4 py-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl transition-colors font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    Partager WhatsApp
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={joinMeeting}
                  className="flex-1 px-4 py-2.5 bg-clinic-green hover:bg-clinic-green-dark text-white rounded-xl transition-colors font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-login-circle-line"></i>
                  Rejoindre la réunion
                </button>
                <button
                  onClick={() => { setMeetingLink(''); setMeetingCode(''); setError(''); }}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm whitespace-nowrap"
                >
                  Créer un autre lien
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}